import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import classNames from 'classnames/bind';
import { Button } from '@saucelabs/sl-component-buttons';
import { IconArrowUp } from '@saucelabs/sl-component-icons';
import { RegressionInfo } from '@saucelabs/sl-component-performance-preview';

import RegressionItem from './RegressionItem';
import Styles from './RegressionList.styles';
import SelectAllItem from './SelectAllItem';
import RegressionListBottom from './RegressionListBottom';

const context = classNames.bind(Styles);

const RegressionList = ({
  regressionData,
  getPerformanceURL,
  containerClass,
  className,
  onActionButtonClick,
  regressionType,
  onViewCommands,
  onResetClick,
  onResetSuccess,
  loadingAcknowledgment,
  onAcknowledgmentSuccess,
  failureAcknowledgment,
  failureReset,
  loadingReset,
}) => {
  const [selectedItems, updateSelection] = useState({});
  const [
    currentLoadingAcknowledgment, updateLoadingAcknowledgment,
  ] = useState(loadingAcknowledgment);
  const filteredRegressionList = regressionData.filter((regressionInfo) => (
    regressionInfo.regressionType === regressionType),
  );

  useEffect(() => {
    // If loading value change from true to false and failureAcknowledgment is null
    // then execute success callback.
    if (currentLoadingAcknowledgment && !loadingAcknowledgment && !failureAcknowledgment) {
      onAcknowledgmentSuccess();
    }
    updateLoadingAcknowledgment(loadingAcknowledgment);
  }, [loadingAcknowledgment, failureAcknowledgment]);

  const updateItemSelection = (orderIndex, isChecked) => {
    updateSelection({
      ...selectedItems,
      [orderIndex]: isChecked,
    });
  };

  const selectAll = ({ target: { checked } }) => {
    const updatedItems = filteredRegressionList.reduce((acc, { order_index: orderIndex }) => {
      acc[orderIndex] = checked;
      return acc;
    }, {});
    updateSelection(updatedItems);
  };

  const handleAdjustBaselineClick = () => {
    const orderIndexes = Object.keys(selectedItems)
      .filter((orderIndex) => !!selectedItems[orderIndex])
      .map((orderIndex) => Number(orderIndex));

    onActionButtonClick({
      regressionType,
      orderIndexes,
    });
  };

  const isItemSelected = Object.keys(selectedItems).some((item) => !!selectedItems[item]);

  return (
    <section className={context('regression-list-container', containerClass)}>
      <header className={Styles['regression-top-bar']}>
        <Button
          category="primary"
          className={Styles['view-commands-button']}
          material
          onClick={onViewCommands}
        >
          <IconArrowUp className={Styles['back-arrow']} />
          {' '}
          view all commands
        </Button>
        <RegressionInfo regressionType={regressionType} />
        <SelectAllItem onCheckboxUpdate={selectAll} />
      </header>
      <div className={context('regression-list', className)}>
        {filteredRegressionList.map((regressionInfo) => (
          <RegressionItem
            key={regressionInfo.order_index}
            getPerformanceURL={getPerformanceURL}
            isChecked={selectedItems[regressionInfo.order_index]}
            onCheckboxUpdate={updateItemSelection}
            regressionData={regressionInfo}
            regressionType={regressionType}
          />
        ))}
      </div>
      <RegressionListBottom
        disableActionButton={!isItemSelected}
        failureReset={failureReset}
        loading={loadingAcknowledgment}
        loadingReset={loadingReset}
        onActionButtonClick={handleAdjustBaselineClick}
        onResetClick={onResetClick}
        onResetSuccess={onResetSuccess}
        regressionType={regressionType}
      />
    </section>
  );
};

RegressionList.propTypes = {
  className: PropTypes.string,
  containerClass: PropTypes.string,
  failureAcknowledgment: PropTypes.object,
  failureReset: PropTypes.object,
  getPerformanceURL: PropTypes.func.isRequired,
  loadingAcknowledgment: PropTypes.bool.isRequired,
  loadingReset: PropTypes.bool.isRequired,
  onAcknowledgmentSuccess: PropTypes.func.isRequired,
  onActionButtonClick: PropTypes.func.isRequired,
  onResetClick: PropTypes.func.isRequired,
  onResetSuccess: PropTypes.func.isRequired,
  onViewCommands: PropTypes.func.isRequired,
  regressionData: ImmutablePropTypes.list.isRequired,
  regressionType: PropTypes.string.isRequired,
};

RegressionList.defaultProps = {
  className: null,
  containerClass: null,
  failureAcknowledgment: null,
  failureReset: null,
};

export default RegressionList;
